import {
  throwIfHtmlResponse,
  HttpResponseError
} from '@/assets/js/helpers/httpResponseError';

export const state = () => ({
  currentUser: {},
  isAuthenticated: false
});

export const mutations = {
  setUser(state, currentUser) {
    state.currentUser = { ...state.currentUser, ...currentUser };
    state.isAuthenticated = !!currentUser;
  },
  clearAuth(state) {
    state.currentUser = {};
    state.isAuthenticated = false;
  },
  setUserPermissions(state, permissions) {
    state.currentUser = { ...state.currentUser, permissions };
  },
  setFirstName(state, firstname) {
    state.currentUser = { ...state.currentUser, firstname };
  },
  setLastName(state, lastname) {
    state.currentUser = { ...state.currentUser, lastname };
  },
  setMiddleName(state, middlename) {
    state.currentUser = { ...state.currentUser, middlename };
  },
  setEmail(state, email) {
    state.currentUser = { ...state.currentUser, email };
  },
  setUserState(state, userState) {
    state.currentUser = { ...state.currentUser, state: userState };
  },
  setUserCity(state, userCity) {
    state.currentUser = { ...state.currentUser, city: userCity };
  },
  setUserCountry(state, userCountry) {
    state.currentUser = { ...state.currentUser, country: userCountry };
  }
};

export const actions = {
  async login(_, { credentials, domain }) {
    try {
      let response = await this.$travellingSDK.AuthDomain.login(
        {
          email: credentials.email,
          password: credentials.password
        },
        domain
      );

      const isSuppressedHtml = throwIfHtmlResponse(response);

      if (!isSuppressedHtml && (!response || typeof response !== 'object')) {
        throw new Error('Received unexpected response from login endpoint');
      } else if (isSuppressedHtml) {
        // If the response is HTML & suppressed, set the response to an empty object For UI display purposes
        response = {};
      }

      const loginErrors = {
        locked: `This account has been locked.<br/>Please <a href="/login/forgot">reset your password</a> to login.`,
        'password-error':
          'Password does not meet requirements. Please verify and resubmit.',
        'login-error':
          'Email or password does not match our records.<br/>Please verify and resubmit.'
      };

      if (!response.msg || response.type) {
        response.error =
          loginErrors[response.type] || 'Login failed due to an unknown error.';
      }
      return response;
    } catch (error) {
      const metadata = {
        page: 'store/auth.js',
        method: 'login',
        endpoint: '$travellingSDK.AuthDomain.login'
      };

      if (error instanceof HttpResponseError) {
        metadata.statusCode = error.statusCode;
        metadata.source = error.source;
        metadata.rayId = error.rayId;
      }

      this.$bugsnag?.notify(error, event => {
        event.addMetadata('Extra Info', metadata);
      });
      throw error;
    }
  },
  async fetchUser({ commit, dispatch }) {
    let response;
    try {
      response = await this.$travellingSDK.UserCurrent.get();

      if (
        !response ||
        typeof response === 'string' ||
        response?.type?.includes('error')
      ) {
        throw new Error(response);
      }

      commit('setUser', response);
    } catch (error) {
      this.$bugsnag?.notify(error, event => {
        event.addMetadata('Extra Info', {
          action: 'fetchUser',
          endpoint: '$travellingSDK.UserCurrent.get',
          response
        });
      });
      console.error('Fetch user failed', error);
      dispatch('logout');
    }
  },
  async logout({ commit }) {
    try {
      await this.$travellingSDK.Auth.logout();
      if (process.server) {
        state.currentUser = {};
        state.isAuthenticated = false;
      } else {
        commit('clearAuth');
      }

      this.$router.push({ name: 'login' });
    } catch (err) {
      if (!(err instanceof Error)) {
        err = new Error(err);
      }
      this.$bugsnag?.notify(err, event => {
        event.addMetadata('Extra Info', {
          action: 'logout',
          endpoint: '$travellingSDK.Auth.logout'
        });
      });
      console.error('Logout failed', err);
    }
  },
  async checkUserPermissions({ commit, state }) {
    const routes = [
      {
        feature: 'peopleReport',
        method: 'GET',
        route: `/permissions/product/people_reports`
      },
      {
        feature: 'emailReport',
        method: 'GET',
        route: `/permissions/product/email_reports`
      },
      {
        feature: 'addressReport',
        method: 'GET',
        route: `/permissions/product/address_reports`
      },
      {
        feature: 'phoneReport',
        method: 'GET',
        route: `/permissions/product/phone_reports`
      },
      {
        feature: 'sexOffenderReport',
        method: 'GET',
        route: `/permissions/product/sexoffender_reports`
      },
      {
        feature: 'generatePdf',
        method: 'GET',
        route: `/permissions/product/pdf_generation`
      },
      {
        feature: 'reportMonitoring',
        method: 'GET',
        route: `/permissions/product/report_monitoring`
      }
    ];
    const routesFormattedForEndpoint = [];
    routes.forEach(route => {
      routesFormattedForEndpoint.push({
        method: route.method,
        route: route.route
      });
    });

    let checkedRoutes = [];
    let hasValidPermissionResponse = false;

    try {
      checkedRoutes = await this.$travellingSDK.UserCurrent.routesCheck(
        routesFormattedForEndpoint
      );
      hasValidPermissionResponse =
        Array.isArray(checkedRoutes) && checkedRoutes.length > 0;

      if (!hasValidPermissionResponse) {
        throw new Error('Invalid permissions response');
      }
    } catch (error) {
      this.$bugsnag?.notify(error, event => {
        event.addMetadata('Extra Info', {
          action: 'checkUserPermissions',
          endpoint: '$travellingSDK.UserCurrent.routesCheck',
          response: checkedRoutes?.msg || checkedRoutes
        });
      });
      console.error('Failed to check user permissions:', error);
    }

    // Avoid overwriting existing permissions with all-false values when
    // permissions endpoint fails or returns an invalid payload.
    if (!hasValidPermissionResponse) {
      const existingPermissions = state.currentUser?.permissions;

      if (existingPermissions && Object.keys(existingPermissions).length) {
        return existingPermissions;
      }

      return null;
    }

    const permissions = {};

    routes.forEach(route => {
      const match = checkedRoutes.find(
        returnedRoute => returnedRoute.route === route.route
      );
      permissions[route.feature] = !!match?.allowed;
    });

    commit('setUserPermissions', permissions);

    return permissions;
  }
};

export const getters = {
  currentUser: state => state.currentUser,
  isAuthenticated: state => state.isAuthenticated,
  hasPeoplePermission: state => state.currentUser?.permissions?.peopleReport,
  hasEmailPermission: state => state.currentUser?.permissions?.emailReport,
  hasAddressPermission: state => state.currentUser?.permissions?.addressReport,
  hasPhonePermission: state => state.currentUser?.permissions?.phoneReport,
  hasOffenderPermission: state =>
    state.currentUser?.permissions?.sexOffenderReport,
  hasEnabledPdf: state => state.currentUser?.permissions?.generatePdf,
  hasEnabledMonitoring: state =>
    state.currentUser?.permissions?.reportMonitoring
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
